import { useCallback } from "react";
import ChevronUpIcon from "@heroicons/react/20/solid/ChevronUpIcon";
import { useMutation } from "@tanstack/react-query";

import cx from "@hsl/core/utils/cx";
import { createFundWatchData } from "@hsl/fc-subscription/lib";
import useFCSubscriptionStore from "@hsl/fc-subscription/store";

import useTranslation, { Translate } from "../../../hooks/useTranslation";
import {
    SubscribableAssetsService,
    type SubscribableAssetUpdate,
} from "../../../openapi";
import { ExplorerStoreContext } from "../../../store/store";
import Button from "../../Button";
import Collapsible from "../../Collapsible";
import SvgShareIcon from "../../icons/Share";
import Spinner from "../../Spinner";
import { txStrReplacer as replacer } from "../lib";
import { type FundCentres } from "../useFundCentres";
import { AcceptFundRow } from "./AcceptFundRow";
import { Table } from "./Table";

interface Props {
    fundCentres: FundCentres;
    className?: string;
    startOpen?: boolean;
}

export const AcceptFunds = ({ fundCentres, className, startOpen }: Props) => {
    const userId = useFCSubscriptionStore((state) => state.user?.userId);
    const subscriptions = useFCSubscriptionStore(
        (state) => state.userSubscriptions,
    );
    const refreshSubscriptions = useFCSubscriptionStore(
        (state) => state.refreshUserSubscriptions,
    );

    const pendingSubscriptions = subscriptions.filter(
        (sub) => sub.subscriptionStatus === "PENDING",
    );
    const tx = useTranslation();
    const subTitleSingular = tx("[[N]] fund has been shared with you").result;
    const subTitlePlural = tx("[[N]] funds have been shared with you").result;

    const pendingFunds = new Set(pendingSubscriptions.map((sub) => sub.fundId));

    const { mutate: acceptAllSubscriptions, isLoading: isAcceptingAll } =
        useMutation({
            mutationFn: async () => {
                if (!userId) throw new Error("No user");

                const pendingFundsWithoutWatching = Array.from(pendingFunds)
                    .filter(
                        (fundId) =>
                            !subscriptions.some(
                                (sub) =>
                                    sub.fundId === fundId &&
                                    sub.subscriptionType === "WATCHLIST",
                            ),
                    )
                    .map((fundId) => {
                        try {
                            const [type, id] = fundId.split("-") as [
                                string,
                                string,
                            ];
                            const query = fundCentres.find(
                                (fc) =>
                                    fc.data?.type == type &&
                                    fc.data?.funds.includes(id),
                            );
                            if (!query?.data?.store) return null;
                            return query.data.store.getState().getFundById(id);
                        } catch {
                            return null;
                        }
                    })
                    .filter(Boolean);

                if (pendingFundsWithoutWatching.length > 0) {
                    await SubscribableAssetsService.createSubscriptions(
                        userId,
                        pendingFundsWithoutWatching.map((fund) =>
                            createFundWatchData(fund!),
                        ),
                    );
                }

                await SubscribableAssetsService.updateSubscriptions(
                    userId,
                    pendingSubscriptions.map(
                        (sub) =>
                            ({
                                subscriptionId: sub.subscriptionId,
                                subscriptionStatus: "CONFIRMED",
                                frequency: sub.frequency,
                                subscriptionType: sub.subscriptionType,
                            }) as SubscribableAssetUpdate,
                    ),
                );
                await refreshSubscriptions();
            },
        });

    const renderLabel = useCallback(
        (open: boolean) => (
            <div className="w-full">
                <div className="flex gap-3">
                    <SvgShareIcon className="text-core-yellow" />
                    <div>
                        <p className="text-lg font-medium">
                            <Translate>Funds shared with you</Translate>
                        </p>
                        <p className="text-core-blue text-baes">
                            {replacer(
                                pendingFunds.size > 1
                                    ? subTitlePlural
                                    : subTitleSingular,
                                { n: pendingFunds.size },
                            )}
                        </p>
                    </div>
                </div>
                {open && (
                    <Button
                        color="primary"
                        className="absolute right-14 top-6 font-normal max-md:hidden"
                        onClick={() => acceptAllSubscriptions()}
                        disabled={isAcceptingAll}
                    >
                        <Translate>Accept all</Translate>
                    </Button>
                )}
                <ChevronUpIcon
                    className={cx(
                        "text-taupe-grey absolute right-4 top-4 h-6 w-6 transition-transform",
                        !open && "rotate-180",
                    )}
                />
            </div>
        ),
        [
            pendingFunds.size,
            subTitlePlural,
            subTitleSingular,
            isAcceptingAll,
            acceptAllSubscriptions,
        ],
    );

    if (pendingSubscriptions.length === 0) return null;

    const fundsWithPendingSubscriptions = [];
    const fundsWithPendingWatchlists = [];

    for (const fund of pendingFunds) {
        if (
            pendingSubscriptions.some(
                (sub) => sub.fundId === fund && sub.subscriptionType === "FULL",
            )
        ) {
            fundsWithPendingSubscriptions.push(fund);
        } else {
            fundsWithPendingWatchlists.push(fund);
        }
    }

    return (
        <div
            className={cx(
                "relative w-full bg-white p-4 shadow ring-1 ring-black/5",
                className,
            )}
        >
            <Collapsible label={renderLabel} labelAs="div" isOpen={startOpen}>
                <div className="w-full pt-4">
                    {fundCentres.some((fc) => !fc.data && fc.isLoading) ? (
                        <div className="my-4">
                            <Spinner />
                        </div>
                    ) : (
                        <>
                            {fundsWithPendingSubscriptions.length > 0 && (
                                <>
                                    <h2 className="my-3 text-lg font-medium">
                                        <Translate>Subscriptions</Translate>
                                    </h2>
                                    <p className="my-1 max-w-4xl">
                                        <Translate>
                                            The following subscriptions have
                                            been suggested for you. If you
                                            accept, you will receive email
                                            notifications when the selected
                                            documents are published or updated.
                                        </Translate>
                                    </p>
                                    <Table showSelectedDocuments>
                                        {fundsWithPendingSubscriptions.map(
                                            (fundId) => {
                                                const [type, id] = fundId.split(
                                                    "-",
                                                ) as [string, string];
                                                const subscriptions =
                                                    pendingSubscriptions.filter(
                                                        (sub) =>
                                                            sub.fundId ===
                                                            fundId,
                                                    );
                                                const query = fundCentres.find(
                                                    (fc) =>
                                                        fc.data?.type == type &&
                                                        fc.data?.funds.includes(
                                                            id,
                                                        ),
                                                );
                                                if (!query?.data?.store)
                                                    return null;
                                                return (
                                                    <ExplorerStoreContext.Provider
                                                        value={query.data.store}
                                                        key={fundId}
                                                    >
                                                        <AcceptFundRow
                                                            type={type}
                                                            fundId={id}
                                                            subscriptions={
                                                                subscriptions
                                                            }
                                                            showSelectedDocuments
                                                        />
                                                    </ExplorerStoreContext.Provider>
                                                );
                                            },
                                        )}
                                    </Table>
                                </>
                            )}
                            {fundsWithPendingWatchlists.length > 0 && (
                                <>
                                    <h2 className="mb-3 mt-4 text-lg font-medium">
                                        <Translate>Watchlist</Translate>
                                    </h2>
                                    <p className="my-1 max-w-4xl">
                                        <Translate>
                                            The following funds have been
                                            suggested for you to watch. If you
                                            accept, they will be added to your
                                            watchlist.
                                        </Translate>
                                    </p>
                                    <Table>
                                        {fundsWithPendingWatchlists.map(
                                            (fundId) => {
                                                const [type, id] = fundId.split(
                                                    "-",
                                                ) as [string, string];
                                                const subscriptions =
                                                    pendingSubscriptions.filter(
                                                        (sub) =>
                                                            sub.fundId ===
                                                            fundId,
                                                    );
                                                const query = fundCentres.find(
                                                    (fc) =>
                                                        fc.data?.type == type &&
                                                        fc.data?.funds.includes(
                                                            id,
                                                        ),
                                                );
                                                if (!query?.data?.store)
                                                    return null;
                                                return (
                                                    <ExplorerStoreContext.Provider
                                                        value={query.data.store}
                                                        key={fundId}
                                                    >
                                                        <AcceptFundRow
                                                            type={type}
                                                            fundId={id}
                                                            subscriptions={
                                                                subscriptions
                                                            }
                                                        />
                                                    </ExplorerStoreContext.Provider>
                                                );
                                            },
                                        )}
                                    </Table>
                                </>
                            )}
                        </>
                    )}
                </div>
            </Collapsible>
        </div>
    );
};
